import * as React from "react"

import { cva } from "../cva.ts"
import { useLayoutEffect } from "../hooks/use-layout-effect.ts"
import { withClass } from "../to-wrapped-children.ts"
import { useFieldRefs, type Field } from "./field.tsx"

export const Validity = ({ state, ...props }: Validity.Props) => {
  const field = useFieldRefs()

  return __shouldRender(field.validityState, state) ? (
    <span
      id={field.uuid("validity")}
      children={field.validationMessage}
      {...withClass(props, classForValidity)}
    />
  ) : null
}

export namespace Validity {
  export type Props = React.ComponentProps<"span"> & {
    state?: Field.Validity
  }
}

export const classForValidity = cva(["block text-xs text-tomato-11"])

const __shouldRender = (validityState: Field.Validity, state?: Field.Validity) =>
  state === validityState || (validityState !== "valid" && state == null)

// #region Use Validity Ref Hook

export const useValidityRef = <T extends Field.Control>(
  ref?: React.ForwardedRef<T>,
  validate?: Field.Validate<T>,
) => {
  const field = useFieldRefs()
  const controlRef = React.useRef<T | null>(null)
  const validateRef = React.useRef(validate)

  React.useImperativeHandle(field.control, () => controlRef.current!)
  React.useImperativeHandle(ref, () => controlRef.current!)

  useLayoutEffect(() => {
    validateRef.current = validate
  }, [validate])

  useLayoutEffect(() => {
    let dirty = false

    const control = controlRef.current as T

    if (control) {
      let currentValidity = field.validityState
      let validationMessage = field.validationMessage

      const handleInput = () => {
        dirty = true
      }

      const handleChange = () => {
        if (dirty) {
          dirty = false

          const customValidationMessage = validateRef.current?.(control as T) ?? ""

          control.setCustomValidity(customValidationMessage)

          const nextValidity = getCurrentValidity(control)
          const nextValidationMessage = customValidationMessage || control.validationMessage

          if (currentValidity !== nextValidity || validationMessage !== nextValidationMessage) {
            currentValidity = nextValidity

            field.setValidityState(nextValidity)
            field.setValidationMessage(nextValidationMessage)
          }
        }
      }

      const handleInvalid = (event: Event) => {
        event.preventDefault()

        const customValidationMessage = validateRef.current?.(control as T) ?? ""

        control.setCustomValidity(customValidationMessage)

        const validity = getCurrentValidity(control)

        field.control.current = control

        field.setValidityState(validity)
        field.setValidationMessage(control.validationMessage)
      }

      control.addEventListener("input", handleInput)

      control.addEventListener("invalid", handleInvalid)

      control.addEventListener("change", handleChange)
      control.addEventListener("focusout", handleChange)

      return () => {
        control.removeEventListener("input", handleInput)

        control.removeEventListener("invalid", handleInvalid)

        control.removeEventListener("change", handleChange)
        control.removeEventListener("focusout", handleChange)
      }
    }
  }, [])

  return controlRef
}

// #endregion

// #region Get Current Validity

/** Returns ... */
const getCurrentValidity = (control: Field.Control): Field.Validity => {
  for (const type of validityTypes) {
    if (control.validity[type]) {
      return type
    }
  }

  return "valid"
}

/** ... */
const validityTypes: Field.Validity[] = [
  "badInput",
  "customError",
  "patternMismatch",
  "rangeOverflow",
  "rangeUnderflow",
  "stepMismatch",
  "tooLong",
  "tooShort",
  "typeMismatch",
  "valueMissing",
]

// #endregion
